import React from 'react';
import styled from 'styled-components';
import { colors } from '../../globalStyles';
import { TriageCategory } from '../../graphQL';
import { CATEGORY_COLORS, CATEGORY_LABELS } from './constants';
import { humanizeEnumValue } from '../../utils/humanizeEnumValue';

/**
 * Compact triage pill for the Beacon student list.
 *
 * Reads the shared triage palette (`CATEGORY_COLORS`) and labels (`CATEGORY_LABELS`).
 * The labels are the same record the student detail badge (#3932) reads, so the two
 * never disagree on a category's name; the colours resolve to the same scalar constants
 * the badge's own `CATEGORY_STYLES` uses. The palette clears WCAG AA (4.5:1) as 12px
 * pill text on its tint.
 *
 * A category this build does not know can still arrive from a newer API, so it
 * renders (as a humanized label) in a neutral tint rather than borrowing another
 * category's colour and implying the wrong severity.
 */
export const TriagePill = ({ category }: { category: TriageCategory | null | undefined }) => {
  if (!category) {
    return (
      <Pill
        bg={colors.grayTextBg}
        fg={colors.grayText}
        role="img"
        aria-label="No triage category"
        style={{ padding: '0.125rem 1.125rem' }}
      >
        —
      </Pill>
    );
  }

  const tone = CATEGORY_COLORS[category];

  return (
    <Pill bg={tone?.activeColor ?? colors.grayTextBg} fg={tone?.markerColor ?? colors.grayText}>
      {CATEGORY_LABELS[category] ?? humanizeEnumValue(category)}
    </Pill>
  );
};

const Pill = styled.span<{ bg: string; fg: string }>`
  display: inline-block;
  padding: 0.125rem 0.625rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.25rem;
  white-space: nowrap;
  background-color: ${({ bg }) => bg};
  color: ${({ fg }) => fg};
`;
